/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles, IStylesFunctionOrObject } from '@fluentui/foundation-legacy';
import { IStackProps, IStackSlots, IStackTokens, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { getFocusStyle, getTheme, mergeStyles, mergeStyleSets } from '@fluentui/style-utilities';
import * as React from 'react';
import ReactPaginate, { ReactPaginateProps } from 'react-paginate';
import { getScopedTheme, getStylistV2, important, joinClassNames } from 'src/core/themes/luisStylist';

export type TablePaginationProps = Pick<ReactPaginateProps, 'pageCount' | 'onPageChange'> & {
    /**
     * The current page to display.
     */
    currentPage: number;

    /**
     * Extra styling for the container.
     */
    containerStyles?: IStylesFunctionOrObject<IStackProps, IStackTokens, IComponentStyles<IStackSlots>>;
};

const { getClassName } = getStylistV2('TablePagination');
const paginationTheme = getScopedTheme('TablePagination');

const pageClassName = getClassName('PaginationList', theme => ({
    display: 'inline-block',
    margin: theme.pageMargin,
    border: theme.pageLinkBorder,
    borderRadius: theme.pageLinkBorderRadius
}));

const activeClassName = getClassName('ActivePaginationList', { border: 0 });

const disabledClassName = getClassName('PaginationLinkDisabled', theme =>
    important({
        backgroundColor: theme.pageLinkDisabledBackgroundColor,
        color: theme.pageLinkDisabledColor,
        border: theme.pageLinkDisabledBorder
    })
);

const pageLinkClassName = getClassName('PaginationLink', theme => ({
    padding: theme.pageLinkPadding,
    backgroundColor: theme.pageLinkBackgroundColor,
    height: theme.pageLinkHeight,
    cursor: 'pointer',
    display: 'inline-block',
    lineHeight: theme.pageLinkLineHeight,
    textAlign: 'center',
    fontSize: theme.pageLinkFontSize,
    $nest: {
        '&:hover': {
            backgroundColor: theme.pageLinkHoverBackgroundColor
        }
    }
}));

const activeLinkClassName = getClassName('SelectedPaginationLink', theme => ({
    backgroundColor: `${theme.pageActiveLinkBackgroundColor} !important`,
    border: `solid 2px ${theme.pageActiveLinkColor}`,
    color: theme.pageActiveLinkColor
}));

const paginationContainerStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: {
        userSelect: 'none',
        marginTop: `${paginationTheme.containerMargin} !important`,
        marginBottom: `${paginationTheme.containerMargin} !important`
    }
});

const focusClassName = mergeStyles(getFocusStyle(getTheme()));

/**
 * Displays a common pagination component to be used
 * for tables across the portal.
 */
export const TablePagination = (props: TablePaginationProps) => {
    const { pageCount, currentPage, containerStyles, onPageChange } = props;

    return pageCount > 1 ? (
        <Stack styles={mergeStyleSets(paginationContainerStyles, containerStyles)}>
            <Stack horizontal horizontalAlign="center">
                <ReactPaginate
                    forcePage={currentPage}
                    pageCount={pageCount}
                    pageRangeDisplayed={2}
                    marginPagesDisplayed={2}
                    onPageChange={onPageChange}
                    breakClassName={pageClassName}
                    breakLinkClassName={joinClassNames(pageLinkClassName, focusClassName)}
                    pageClassName={pageClassName}
                    activeClassName={activeClassName}
                    previousClassName={pageClassName}
                    nextClassName={pageClassName}
                    pageLinkClassName={joinClassNames(pageLinkClassName, focusClassName)}
                    previousLinkClassName={joinClassNames(pageLinkClassName, focusClassName)}
                    nextLinkClassName={joinClassNames(pageLinkClassName, focusClassName)}
                    activeLinkClassName={activeLinkClassName}
                    disabledClassName={disabledClassName}
                />
            </Stack>
        </Stack>
    ) : null;
};
